<template>
  <h1 class="text-center text-base font-semibold mb-3"> {{ pageTitle }}</h1>
  <form @submit.prevent="handleSubmit" class="flex flex-col gap-2.5">
    <div class="flex flex-col gap-2.5">
      <!-- Term Selector -->
      <div class="flex flex-col gap-1.5">
        <label for="term_structure_id" class="text-xs font-medium text-gray-700">
          Select the Current Term
        </label>
        <Select v-model="formData.term_structure_id" :options="termOptions" optionLabel="label" optionValue="value"
          :placeholder="`Select a ${termLabel}`" class="w-full" />
        <small v-if="errors.term_structure_id" class="text-red-500">{{ errors.term_structure_id }}</small>
      </div>

      <!-- Total School Days -->
      <div class="flex flex-col gap-1.5">
        <label for="total_school_days" class="text-xs font-medium text-gray-700">
          Total School Days
        </label>
        <InputNumber v-model="formData.total_school_days" inputId="total_school_days" class="w-full"
          inputClass="w-full" />
      </div>

      <!-- Start Date -->
      <div class="flex flex-col gap-1.5">
        <label for="started_at" class="text-xs font-medium text-gray-700">
          Start Date & Time
        </label>
        <Calendar v-model="formData.started_at" showTime hourFormat="24" dateFormat="yy-mm-dd" class="w-full"
          :class="{ 'p-invalid': errors.started_at }" />
        <small v-if="errors.started_at" class="text-red-500">{{ errors.started_at }}</small>
      </div>

      <!-- End Date -->
      <div class="flex flex-col gap-1.5">
        <label for="ended_at" class="text-xs font-medium text-gray-700">
          End Date & Time
        </label>
        <Calendar v-model="formData.ended_at" showTime hourFormat="24" dateFormat="yy-mm-dd" class="w-full"
          :class="{ 'p-invalid': errors.ended_at }" />
        <small v-if="errors.ended_at" class="text-red-500">{{ errors.ended_at }}</small>
      </div>
    </div>

    <!-- Next Calendar Start -->
    <div class="flex flex-col gap-1.5">
      <label for="next_calender_starts_at" class="text-xs font-medium text-gray-700">
        Next Calendar Starts At
      </label>
      <Calendar v-model="formData.next_calender_starts_at" showTime hourFormat="24" dateFormat="yy-mm-dd"
        class="w-full" />
    </div>

    <!-- Is Active -->
    <div class="flex items-center gap-1.5">
      <Checkbox v-model="formData.is_active" :binary="true" inputId="is_active" />
      <label for="is_active" class="text-xs text-gray-700 cursor-pointer">Active</label>
    </div>

    <!-- Submit Button -->
    <div class="flex justify-end mt-1">
      <Button type="submit" label="Create Academic Calendar" size="small" :loading="isSubmitting"
        class="!bg-primary !border-primary" />
    </div>
  </form>
</template>

<script setup lang="ts">
import { useUserStore } from '@/store/userStore';
import { useToast } from 'primevue/usetoast';
import { useSchoolMutations } from '@/composables/mutations/school/useSchoolMutations';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { GET_TERM_STRUCTURES, QUERY_ALL_SESSION_STRUCTURES, QUERY_ALL_SESSIONS } from '@/graphql';
import { useQuery } from '@vue/apollo-composable';

import { extractGraphQLError } from '@/utils/graphqlError';
const toast = useToast();

// Store
const userStore = useUserStore();
const { createAcademicCalendar } = useSchoolMutations();
const isSubmitting = ref(false);

const { school, academicCalendars, refetchSchool } = useSchoolQuery();
const { isTertiary } = useSchoolFeatures();
const { result: allSessionStructuresResult } = QUERY_ALL_SESSION_STRUCTURES();
const { result: allSessionsResult } = QUERY_ALL_SESSIONS();

defineProps<{
  currentStep: number
}>();

const emit = defineEmits<{
  (_e: 'update-step', _step: number): void
}>();

const sessionStructureId = computed(() => {
  if (userStore.tempSessionStructureId) return userStore.tempSessionStructureId;
  const structures = allSessionStructuresResult.value?.allSessionStructures || [];
  return structures.length > 0 ? structures[structures.length - 1].id : '';
});

// Fetch term structures based on session structure
const { result: term } = useQuery(GET_TERM_STRUCTURES, () => ({
  session_structure_id: sessionStructureId.value,
}), {
  enabled: computed(() => !!sessionStructureId.value),
});

interface TermStructure {
  id: string;
  name: string;
}

// Format term options for the select field
const termOptions = computed(() =>
  term.value?.termStructuresForStructure?.map((t: TermStructure) => ({
    label: t.name,
    value: t.id,
  })) || [],
);
// Form state
const formData = ref<{
  session_id: string;
  term_structure_id: string;
  started_at: Date | null;
  ended_at: Date | null;
  is_active: boolean;
  total_school_days: number | undefined;
  next_calender_starts_at: Date | null;
}>({
  session_id: '',
  term_structure_id: '',
  started_at: null,
  ended_at: null,
  is_active: true,
  total_school_days: undefined,
  next_calender_starts_at: null,
});

// Form errors
const errors = ref<{ [key: string]: string }>({});

// Validation
const validateForm = (): boolean => {
  errors.value = {};

  if (!formData.value.term_structure_id) {
    errors.value.term_structure_id = 'Term is required.';
  }

  if (formData.value.started_at && formData.value.ended_at) {
    const start = new Date(formData.value.started_at).getTime();
    const end = new Date(formData.value.ended_at).getTime();
    if (start >= end) {
      errors.value.ended_at = 'End date must be after start date.';
    }
  }

  return Object.keys(errors.value).length === 0;
};

// Submit handler
const handleSubmit = async() => {
  if (!validateForm()) return;
  isSubmitting.value = true;

  try {
    let sessionId = userStore.tempSessionId;
    if (!sessionId) {
      const sessions = allSessionsResult.value?.schoolSessions || [];
      if (sessions.length > 0) {
        sessionId = sessions[sessions.length - 1].id;
      }
    }

    if (!sessionId) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Session ID not found. Please complete the previous step.', life: 4000 });
      isSubmitting.value = false;
      emit('update-step', 2); // Go back to session creation
      return;
    }
    formData.value.session_id = sessionId;

    // Helper to format date as YYYY-MM-DD
    const formatDate = (date: string | Date | null | undefined): string => {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const formatted = {
      ...formData.value,
      started_at: formData.value.started_at ? formatDate(formData.value.started_at) : undefined,
      ended_at: formData.value.ended_at ? formatDate(formData.value.ended_at) : undefined,
      next_calender_starts_at: formData.value.next_calender_starts_at
        ? formatDate(formData.value.next_calender_starts_at)
        : undefined,
    };

    await createAcademicCalendar(formatted);

    // Move to step 4 after successful creation
    emit('update-step', 4);

  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(err, 'Submission error'), life: 3000 });
  } finally {
    isSubmitting.value = false;
  }
};

// Watch for modal visibility and refetch classrooms
watch(
  () => academicCalendars.value,
  async() => {
    await refetchSchool();
  },
);

// Dynamic labels
const pageTitle = computed(() => {
  if (isTertiary.value) {
    return 'Create Semester Calendar';
  }
  return 'Create Academic Calendar';
});

const termLabel = computed(() =>
  isTertiary.value
    ? 'Semester'
    : 'Term',
);
</script>
